import { API_URL } from '../config/api';

export interface CreateSubscriptionRequest {
  TariffPlanId: string;
  Months?: number;
  TeamSize?: number;
  TeamName?: string | null;
}

export interface CreateSubscriptionResponse {
  success: boolean;
  subscriptionId?: string;
  redirectUrl?: string;
  error?: string;
}

export interface SubscriptionStatus {
  subscriptionId: string;
  status: string;
  createdAt: string;
  lastChargeAt?: string;
  nextChargeAt?: string;
  successfulCharges: number;
  failedCharges: number;
  amount: number;
  currency: string;
  isActive: boolean;
}

export interface TariffPlan {
  id: string;
  name: string;
  description: string;
  type: 'Individual' | 'Team';
  basePrice: number;
  pricePerMember: number;
  generationsLimitPerUser: number;
  durationInDays: number;
  // Новые лимиты по типам генераций
  audienceAnalysisLimit: number;
  reelsAnalysisLimit: number;
  roadmapLimit: number;
  summaryLimit: number;
  contentPlanLimit: number;
  isTrial: boolean;
}

export const subscriptionApi = {
  /**
   * Получает список доступных тарифных планов
   */
  async getTariffs(): Promise<TariffPlan[]> {
    const response = await fetch(`${API_URL}/subscriptionpayment/tariffs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Ошибка получения тарифов (${response.status})`);
    }

    const raw = await response.json();
    // Приведение кейсов/полей бэка к фронтовой модели
    return raw.map((t: any) => ({
      id: t.tariffPlanId || t.id,
      name: t.name,
      description: t.description,
      type: t.type,
      basePrice: t.basePrice,
      pricePerMember: t.pricePerMember ?? 0,
      generationsLimitPerUser: t.generationsLimitPerUser ?? -1,
      durationInDays: t.durationInDays,
      audienceAnalysisLimit: t.audienceAnalysisLimit ?? -1,
      reelsAnalysisLimit: t.reelsAnalysisLimit ?? -1,
      roadmapLimit: t.roadmapLimit ?? -1,
      summaryLimit: t.summaryLimit ?? -1,
      contentPlanLimit: t.contentPlanLimit ?? -1,
      isTrial: t.isTrial ?? false,
    }));
  },

  /**
   * Создает подписку через Точка Банк
   */
  async createSubscription(request: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse> {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      throw new Error('Необходима авторизация для создания подписки');
    }
    
    const response = await fetch(`${API_URL}/subscriptionpayment/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Сессия истекла. Пожалуйста, войдите в систему заново');
      }
      
      if (response.status === 400) {
        const errorData = await response.json().catch(() => ({ error: 'Ошибка валидации' }));
        
        // Обработка ValidationProblemDetails от ASP.NET Core
        if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat();
          throw new Error(`Ошибка валидации: ${validationErrors.join(', ')}`);
        }
        
        throw new Error(errorData.error || errorData.title || 'Неверные данные запроса');
      }
      
      const errorData = await response.json().catch(() => ({ error: 'Ошибка сети' }));
      throw new Error(errorData.error || `Ошибка создания подписки (${response.status})`);
    }

    return await response.json();
  },

  /**
   * Получает статус подписки по ID
   */
  async getSubscriptionStatus(subscriptionId: string): Promise<SubscriptionStatus> {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      throw new Error('Необходима авторизация');
    }
    
    const response = await fetch(`${API_URL}/subscriptionpayment/status/${subscriptionId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Сессия истекла. Пожалуйста, войдите в систему заново');
      }
      throw new Error(`Ошибка получения статуса подписки (${response.status})`);
    }

    return await response.json();
  },

  /**
   * Отменяет активную подписку пользователя
   */
  async cancelSubscription(): Promise<{ success: boolean; message?: string; error?: string }> {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      throw new Error('Необходима авторизация');
    }
    
    const response = await fetch(`${API_URL}/subscriptionpayment/cancel`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Сессия истекла. Пожалуйста, войдите в систему заново');
      }
      
      const errorData = await response.json().catch(() => ({ error: 'Ошибка сети' }));
      throw new Error(errorData.error || `Ошибка отмены подписки (${response.status})`);
    }

    return await response.json();
  }
};
